import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mountain, Clock, TrendingUp, MapPin, Users, ArrowRight, Calendar, ChevronDown } from 'lucide-react';
import { ITINERARIES, DIFFICULTY_COLORS, VIBE_LABELS } from '../data/itineraryData';

const pf = { fontFamily: "'Playfair Display', Georgia, serif" };

const FILTERS = ['All', 'Easy', 'Moderate', 'Challenging'];
const REGIONS = ['All Regions', 'Himachal Pradesh', 'Uttarakhand', 'Meghalaya'];

export default function Itineraries() {
  const [difficulty, setDifficulty] = useState('All');
  const [region, setRegion] = useState('All Regions');

  const filtered = ITINERARIES.filter(it => {
    if (difficulty !== 'All' && it.difficulty !== difficulty) return false;
    if (region !== 'All Regions' && it.region !== region) return false;
    return true;
  });

  return (
    <div className="min-h-screen bg-[#FAFAF8]" data-testid="itineraries-page">
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-stone-100">
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Mountain className="w-5 h-5 text-[#2C5F8D]" />
            <span className="text-sm font-bold tracking-tight text-stone-800" style={pf}>GoHimalayan</span>
          </Link>
          <div className="flex items-center gap-5 text-xs text-stone-500">
            <Link to="/discovered" className="hover:text-stone-800 transition-colors hidden sm:inline">Stays</Link>
            <span className="text-[#2C5F8D] font-semibold">Itineraries</span>
            <Link to="/partner" className="hover:text-stone-800 transition-colors hidden sm:inline">Partner</Link>
          </div>
        </div>
      </nav>

      {/* Curated invitation strip — anchored above the hero */}
      <Link to="/itineraries/longer-road"
            className="block pt-16"
            data-testid="longer-road-promo-strip">
        <div className="max-w-5xl mx-auto px-6">
          <div className="relative rounded-2xl overflow-hidden group hover:shadow-2xl transition-shadow"
               style={{ backgroundColor: '#0d1a2b' }}>
            {/* Real dusk peaks image */}
            <img
              src="https://images.unsplash.com/photo-1508739773434-c26b3d09e071?auto=format&fit=crop&w=1600&q=80"
              alt=""
              className="absolute inset-0 w-full h-full object-cover"
              style={{ filter: 'brightness(0.55) saturate(1.15)' }}
            />
            {/* Colour wash · valley dusk */}
            <div className="absolute inset-0"
                 style={{ background: 'linear-gradient(90deg, rgba(44,95,141,0.90) 0%, rgba(44,95,141,0.35) 55%, rgba(13,26,43,0.85) 100%)' }} />

            <div className="relative p-4 sm:p-5 flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-6 text-white">
              <div className="text-[10px] tracking-[0.35em] uppercase flex-shrink-0" style={{ color: '#EBC773' }}>
                Curated invite &middot; Early Oct &lsquo;26
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-lg leading-tight mb-1" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                  <strong>The Longer Road &middot; Tirthan Valley &lsquo;26</strong>
                </p>
                <p className="text-stone-100 text-sm italic leading-snug" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                  Twelve couples, three quiet nights on the river &mdash; a curated convoy up from Delhi.
                </p>
              </div>
              <div className="flex-shrink-0 text-[13px] whitespace-nowrap font-semibold hidden sm:block" style={{ color: '#EBC773' }}>See the weekend &rarr;</div>
            </div>
          </div>
        </div>
      </Link>

      {/* Hero */}
      <section className="pt-16 pb-16 px-6">
        <div className="max-w-5xl mx-auto">
          <p className="text-xs tracking-[0.4em] uppercase text-stone-400 mb-3">Curated Journeys</p>
          <h1 className="text-4xl sm:text-5xl font-light italic text-stone-900 leading-tight mb-4" style={pf}>
            Not just where to stay.<br />Where to go.
          </h1>
          <p className="text-stone-500 text-lg max-w-2xl mb-10" style={pf}>
            Multi-day itineraries across the Indian Himalayas — from motorcycle circuits to slow valley trails. 
            Every stop links to a curated GoHimalayan stay. Join a community departure, or go on your own.
          </p>

          {/* Filters */}
          <div className="flex flex-wrap items-center gap-3" data-testid="itinerary-filters">
            <div className="flex items-center gap-1.5 bg-white rounded-full border border-stone-200 px-1 py-1">
              {FILTERS.map(f => (
                <button key={f} onClick={() => setDifficulty(f)} className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${difficulty === f ? 'bg-stone-900 text-white' : 'text-stone-500 hover:text-stone-800'}`} data-testid={`filter-${f.toLowerCase()}`}>{f}</button>
              ))}
            </div>
            <div className="relative">
              <select value={region} onChange={e => setRegion(e.target.value)} className="appearance-none bg-white rounded-full border border-stone-200 px-4 py-2 pr-8 text-xs font-medium text-stone-600 cursor-pointer hover:border-stone-300 transition-colors" data-testid="region-filter">
                {REGIONS.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
              <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-stone-400 pointer-events-none" />
            </div>
          </div>
        </div>
      </section>

      {/* Itinerary Cards */}
      <section className="px-6 pb-24">
        <div className="max-w-5xl mx-auto space-y-8">
          {filtered.map((it, idx) => (
            <Link to={`/itinerary/${it.slug}`} key={it.slug} className="group block" data-testid={`itinerary-card-${it.slug}`}>
              <div className="bg-white rounded-2xl border border-stone-100 overflow-hidden hover:shadow-xl hover:border-stone-200 transition-all duration-300">
                <div className="grid grid-cols-1 md:grid-cols-[2fr_3fr]">
                  {/* Image */}
                  <div className="h-56 md:h-full md:min-h-[280px] relative overflow-hidden">
                    <img src={it.cardImage} alt={it.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
                    <div className="absolute bottom-3 left-4 flex items-center gap-2">
                      <span className={`text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${DIFFICULTY_COLORS[it.difficulty]}`}>{it.difficulty}</span>
                      {it.partnerOpportunity && (
                        <span className="text-[9px] text-white/80 bg-white/15 backdrop-blur-sm px-2 py-0.5 rounded-full">Partnership Opportunity</span>
                      )}
                    </div>
                    <div className="absolute top-3 left-4">
                      <span className="text-[10px] text-white/70 bg-black/30 backdrop-blur-sm px-2.5 py-1 rounded-full">{it.region}</span>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6 md:p-8 flex flex-col justify-between">
                    <div>
                      <h2 className="text-2xl font-light italic text-stone-900 mb-1 group-hover:text-[#2C5F8D] transition-colors" style={pf}>{it.title}</h2>
                      <p className="text-sm text-stone-400 italic mb-4" style={pf}>{it.subtitle}</p>
                      <p className="text-sm text-stone-600 leading-relaxed mb-5 line-clamp-2">{it.description}</p>

                      {/* Stats row */}
                      <div className="flex flex-wrap gap-x-5 gap-y-2 mb-5">
                        <div className="flex items-center gap-1.5 text-xs text-stone-500"><Clock className="w-3.5 h-3.5 text-stone-400" />{it.duration} days</div>
                        <div className="flex items-center gap-1.5 text-xs text-stone-500"><TrendingUp className="w-3.5 h-3.5 text-stone-400" />{it.maxAltitude}</div>
                        <div className="flex items-center gap-1.5 text-xs text-stone-500"><Users className="w-3.5 h-3.5 text-stone-400" />{it.groupSize} people</div>
                        <div className="flex items-center gap-1.5 text-xs text-stone-500"><Calendar className="w-3.5 h-3.5 text-stone-400" />{it.bestSeason}</div>
                      </div>

                      {/* Vibes */}
                      <div className="flex flex-wrap gap-1.5 mb-5">
                        {it.vibe.slice(0, 4).map(v => (
                          <span key={v} className="text-[10px] text-stone-500 bg-stone-100 px-2.5 py-1 rounded-full">{VIBE_LABELS[v] || v}</span>
                        ))}
                      </div>
                    </div>

                    {/* Price + CTA */}
                    <div className="flex items-center justify-between pt-4 border-t border-stone-100">
                      <div>
                        <p className="text-lg font-bold text-stone-900" style={pf}>{it.priceRange}</p>
                        <p className="text-[10px] text-stone-400">{it.pricePer}</p>
                      </div>
                      <div className="flex items-center gap-2 text-[#2C5F8D] text-sm font-medium group-hover:gap-3 transition-all">
                        View Itinerary <ArrowRight className="w-4 h-4" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Link>
          ))}

          {filtered.length === 0 && (
            <div className="text-center py-20">
              <MapPin className="w-8 h-8 text-stone-300 mx-auto mb-3" />
              <p className="text-stone-400 text-sm">No itineraries match your filters.</p>
              <button onClick={() => { setDifficulty('All'); setRegion('All Regions'); }} className="text-[#2C5F8D] text-xs mt-2 hover:underline">Clear filters</button>
            </div>
          )}
        </div>
      </section>

      {/* Brand Partnership CTA */}
      <section className="px-6 pb-24">
        <div className="max-w-5xl mx-auto">
          <div className="bg-gradient-to-br from-[#1a2332] to-[#2E4057] rounded-2xl p-8 md:p-12 text-center">
            <p className="text-xs tracking-[0.4em] uppercase text-white/30 mb-4">For Brands</p>
            <h2 className="text-2xl sm:text-3xl font-light italic text-white mb-4" style={pf}>Co-curate a Himalayan experience<br />with your brand.</h2>
            <p className="text-white/50 text-sm max-w-xl mx-auto mb-8" style={pf}>Royal Enfield circuits. Thar expedition routes. Luxury driving trails with Volkswagen or Mercedes-Benz. Decathlon trek camps. GoHimalayan provides the stays, the storytelling, and the community.</p>
            <div className="flex flex-wrap justify-center gap-4">
              {['Royal Enfield', 'Mahindra Adventure', 'Volkswagen', 'Mercedes-Benz', 'Decathlon', 'The North Face'].map(b => (
                <span key={b} className="text-[10px] text-white/40 border border-white/10 px-3 py-1.5 rounded-full">{b}</span>
              ))}
            </div>
            <p className="text-white/25 text-xs mt-6">Interested? Write to partnerships@gohimalayan.com</p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-stone-200 py-6 px-6 bg-white">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Mountain className="w-4 h-4 text-stone-400" />
            <span className="text-xs text-stone-400">© 2026 GoHimalayan</span>
          </div>
          <div className="flex items-center gap-4 text-[10px] text-stone-400">
            <Link to="/privacy" className="hover:text-stone-600 transition-colors">Privacy</Link>
            <Link to="/terms" className="hover:text-stone-600 transition-colors">Terms</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
